'use client';

import { Box, Flex, FormHelperText } from '@coolblue-development/freddy';
import { Rules } from '@sharedTypes';
import React, { useEffect, useState } from 'react';
import { useController } from 'react-hook-form';

import { FormLabelWithInfo } from '../FormLabelWithInfo';
import { MatrixCards } from './MatrixCards';
import { MatrixTable } from './MatrixTable';

type Props = {
  name: string;
  rules?: Rules;
  label: string;
  info?: string;
  columnLabels: string[];
  rowLabels: string[];
  firstRowSelectsAll?: boolean;
};

export type MatrixValuesType = Map<string, Map<string, boolean>>;

export const MatrixHelper = ({
  name,
  rules,
  label,
  info,
  columnLabels,
  rowLabels,
  firstRowSelectsAll = false
}: Props) => {
  const newRowMap = new Map(
    (firstRowSelectsAll ? rowLabels.slice(1) : rowLabels).map(row => [row, false])
  );
  const [matrixValues, setMatrixValues] = useState<MatrixValuesType>(
    columnLabels.reduce((acc, column) => {
      acc.set(column, new Map(newRowMap));
      return acc;
    }, new Map())
  );

  const control = useController({
    name,
    rules: {
      validate: (values: MatrixValuesType) => {
        if (values.values().some(column => column.values().some(value => value))) {
          return true;
        }
        return rules?.required?.message ?? true;
      }
    },
    defaultValue: matrixValues
  });

  useEffect(() => {
    // Don't trigger update and validation when values are the same
    if (control.field.value === matrixValues) return;

    control.field.onChange(matrixValues);
    control.field.onBlur(); // Trigger validation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matrixValues]);

  return (
    <Box>
      <Flex component='fieldset' direction='column' gap={100}>
        <FormLabelWithInfo label={label} info={info} />

        <Box hideUntil='md'>
          <MatrixTable
            matrixValues={matrixValues}
            setMatrixValues={setMatrixValues}
            columnLabels={columnLabels}
            rowLabels={rowLabels}
            firstRowSelectsAll={firstRowSelectsAll}
          />
        </Box>
        <Box hideFrom='md'>
          <MatrixCards
            matrixValues={matrixValues}
            setMatrixValues={setMatrixValues}
            columnLabels={columnLabels}
            rowLabels={firstRowSelectsAll ? rowLabels.slice(1) : rowLabels}
          />
        </Box>

        {control.fieldState.error && (
          <FormHelperText validation='error'>{control.fieldState.error?.message}</FormHelperText>
        )}
      </Flex>
    </Box>
  );
};
